import { Box, Stack } from "@mui/material";
import { useSyncAccountsWhenRegionReady } from "Hooks/useSyncAccountsWhenRegionReady";
import { useGetMerchantAppProductsQuery } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";
import { BalanceWithPaymentActions } from "Components/Dashboard/BalanceWithPaymentActions/BalanceWithPaymentActions";
import { ConnectedAccounts } from "Components/Dashboard/ConnectedAccounts/ConnectedAccounts";
import { DocumentExpirationWarningPopup } from "Components/Dashboard/DocumentExpirationWarningPopup";
import { RelatedPartyExpirationWarningPopup } from "Components/Dashboard/RelatedPartyExpirationWarningPopup";
import { LimitedAccountPopup } from "Components/Dashboard/LimitedAccountPopup";
import { PreApprovalWidget } from "Components/Dashboard/PreApproval/PreApprovalWidget";
import { UnpaidSettlementPopup } from "Components/Dashboard/UnpaidSettlementPopup";
import { TransactionFees } from "Components/Dashboard/TransactionFees/TransactionFees";
import { TransactionsComponent } from "Components/Dashboard/TransactionsComponent";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { HeroSection } from "Components/Layout/HeroBackground/HeroSection";
import { MobileAppInstallBanner } from "Components/MobileApp/MobileAppInstallBanner/MobileAppInstallBanner";
import { OrbitLDashboardModule } from "Components/Merchant/OrbitL/OrbitLDashboardModule";
import { isComgateProductCode } from "Components/Shared/Merchant/Products/AppProducts/constants";
import { BlDivider } from "Components/Shared/BlDivider";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { SignUpSubsidyPopup } from "Components/Voucher/SignUpSubsidyPopup";
import { useIsMobile } from "Hooks/useIsMobile";
import { resetTransactionListPagination } from "State/Transactions/TransactionReducer";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Resources, useResource } from "Translations/Resources";
import { MerchantSetupChecklistCard } from "Components/Dashboard/SetupChecklist/MerchantSetupChecklistCard";
import { PendingInverseTransactions } from "Components/Transactions/PendingInverseTransactions";
import { useBankConnectionSuccessHandler } from "Hooks/useBankConnectionSuccessHandler";
import { AccountSelectionDialog } from "Components/Banking/AccountSelectionDialog/AccountSelectionDialog";
import { AffiliateEarningsSummary } from "Components/Affiliate/Earnings/AffiliateEarningsSummary";
import { HandleMissingBanner } from "Components/Social/HandleMissingBanner";

export const MerchantDashboardPage = () => {
  useSyncAccountsWhenRegionReady();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { t } = useResource();
  const { data: merchant } = useGetMerchantQuery();
  const { data: appProducts } = useGetMerchantAppProductsQuery();

  const hasComgateProduct = appProducts?.some((ap) =>
    isComgateProductCode(ap.productDto.code),
  );

  const {
    shouldShowDialog,
    isDialogOpen,
    bankAccounts,
    bankName,
    isLoading,
    onClose,
    onConfirm,
    isPending,
  } = useBankConnectionSuccessHandler();

  useEffect(() => {
    return () => {
      dispatch(resetTransactionListPagination());
    };
  }, [dispatch]);

  return (
    <AuthenticatedLayout
      title={t(Resources.Pages.Titles.Dashboard)}
      isTitleHiddenBelowLgScreen
      isPaddingDisabled={isMobile}
    >
      <StyledPageWrapper>
        <Box className="left-content">
          <Stack mb={6} mx={isMobile ? 1 : 0} gap={isMobile ? 2 : 1}>
            <HeroSection gap={isMobile ? 2 : 1}>
              <BalanceWithPaymentActions />
            </HeroSection>
            <HandleMissingBanner />
            <PreApprovalWidget />
            <MobileAppInstallBanner />
            <MerchantSetupChecklistCard />
            {hasComgateProduct && merchant?.publicID && (
              <>
                <BlDivider />
                <OrbitLDashboardModule merchantPublicID={merchant.publicID} />
              </>
            )}
            <PendingInverseTransactions />
            <TransactionFees />
            <LimitedAccountPopup />
            <DocumentExpirationWarningPopup />
            <RelatedPartyExpirationWarningPopup />
            <UnpaidSettlementPopup />
            <Stack mt={isMobile ? 2 : 1}>
              <AffiliateEarningsSummary isRegisteredUsersCountHidden />
            </Stack>
            <ConnectedAccounts />
            <BlDivider />
            <Box mx={2}>
              <TransactionsComponent />
            </Box>
            <SignUpSubsidyPopup />
          </Stack>
        </Box>
      </StyledPageWrapper>
      {shouldShowDialog && bankAccounts && (
        <AccountSelectionDialog
          open={isDialogOpen}
          onClose={onClose}
          onConfirm={onConfirm}
          accounts={bankAccounts}
          bankName={bankName}
          isLoading={isLoading}
          isPending={isPending}
        />
      )}
    </AuthenticatedLayout>
  );
};

export default MerchantDashboardPage;
